import React from "react"
import { Form } from "react-bootstrap"
import { Formik, Field, FormikHelpers } from "formik"
import * as Yup from "yup"

import FormikSelect from "~/library/FormikSelect"
import CurrencyInput from "~/library/CurrencyInput"
import SelectOrCreate from "~/library/SelectOrCreate"
import DateSelector from "~/library/DateSelector"
import getAppConfig from "~/utils/getAppConfig"
import { apiSponsorshipCompanies } from "~/routes"
import Company from "~/types/sponsorship/Company"
import { Area } from "~/types/sponsorship/Area"

interface SponsorshipFormProps {
  onSubmit: (values: any, helpers: FormikHelpers<any>) => void
  onCancel: () => void
  onDelete?: () => void
  initialValues?: any
  isEditing?: boolean
  areas?: Area[]
  isLoadingAreas?: boolean
  areaInfo?: {
    areaType: string
    areaId?: string
    cellDate?: string
  } | null
}

const validationSchema = Yup.object({
  startOn: Yup.string().required("Start date is required"),
  endOn: Yup.string().required("End date is required"),
  areaId: Yup.string().required("Area is required"),
  companyId: Yup.string().when("companyAttributes.name", {
    is: (name: string) => !name,
    then: (schema) => schema.required("Company is required"),
    otherwise: (schema) => schema.nullable(),
  }),
  priceCents: Yup.number().min(0, "Price must be positive").required("Price is required"),
  quantity: Yup.number().when("$areaType", {
    is: "physical",
    then: (schema) =>
      schema
        .min(1, "Quantity must be at least 1 for physical areas")
        .required("Quantity is required for physical areas"),
    otherwise: (schema) => schema.nullable(),
  }),
})

const SponsorshipForm: React.FC<SponsorshipFormProps> = ({
  onSubmit,
  onCancel,
  onDelete,
  initialValues,
  isEditing = false,
  areas = [],
  isLoadingAreas = false,
  areaInfo,
}) => {
  const { venueSlug } = getAppConfig()

  const defaultValues = {
    startOn: areaInfo?.cellDate || "",
    endOn: areaInfo?.cellDate || "",
    areaId: areaInfo?.areaId || "",
    companyId: "",
    priceCents: "",
    quantity: "",
    companyAttributes: {
      name: "",
      contactPerson: "",
      email: "",
      phone: "",
    },
  }

  const formInitialValues =
    isEditing && initialValues
      ? {
          startOn: initialValues.startOn,
          endOn: initialValues.endOn,
          areaId: initialValues.area.uuid,
          companyId: initialValues.company.id,
          priceCents: initialValues.price.cents.toString(),
          quantity: initialValues.quantity?.toString() || "",
          companyAttributes: {
            name: "",
            contactPerson: "",
            email: "",
            phone: "",
          },
        }
      : defaultValues

  const areaOptions = Array.isArray(areas)
    ? [
        ...areas.map((area) => ({
          value: area.uuid,
          label: `${area.name} (${area.areaType})`,
        })),
      ]
    : []

  const companyCreateFields: Array<{
    name: keyof Company
    label: string
    type: "text" | "number" | "email"
    required?: boolean
  }> = [
    { name: "name", label: "Company Name", type: "text", required: true },
    { name: "contactPerson", label: "Contact Person", type: "text", required: true },
    { name: "email", label: "Email", type: "email" },
    { name: "phone", label: "Phone", type: "text" },
  ]

  return (
    <Formik
      initialValues={formInitialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      context={{
        areaType: areas.find((area) => area.uuid === formInitialValues.areaId)?.areaType,
      }}
    >
      {({ handleSubmit, values, isSubmitting }) => {
        const selectedArea = areas.find((area) => area.uuid === values.areaId)
        const isPhysicalArea = selectedArea?.areaType === "physical"

        return (
          <Form onSubmit={handleSubmit} className="mb-3">
            <FormikSelect
              name="areaId"
              label="Sponsorship Area *"
              options={areaOptions}
              helpText=""
              disabled={isLoadingAreas}
              placeholder={isLoadingAreas ? "Loading areas..." : "Select an area..."}
            />

            <Form.Group className="mb-3">
              <Form.Label>Company *</Form.Label>
              <SelectOrCreate<Company>
                loadAllInitially={true}
                name="companyId"
                attributesPrefix="companyAttributes"
                endpoint={apiSponsorshipCompanies.index.path({ venueSlug })}
                searchParam="name_cont"
                responseKey="sponsorshipCompanies"
                placeholder="Select or create a company..."
                allowCreate={true}
                createFields={companyCreateFields}
                labelKey="name"
              />
            </Form.Group>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <CurrencyInput
                    name="priceCents"
                    label="Price"
                    placeholder="0.00"
                    currency="USD"
                  />
                </Form.Group>
              </div>
              {isPhysicalArea && (
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Quantity *
                      <small className="text-muted">
                        {" "}
                        (Slots left: {selectedArea?.availableQuantity})
                      </small>
                    </Form.Label>
                    <Field name="quantity">
                      {({ field, meta }: any) => (
                        <>
                          <Form.Control
                            {...field}
                            type="number"
                            min="1"
                            max={selectedArea?.maxQuantity}
                            step="1"
                            placeholder="1"
                            isInvalid={meta.touched && meta.error}
                          />
                          {meta.touched && meta.error && (
                            <Form.Control.Feedback type="invalid">
                              {meta.error}
                            </Form.Control.Feedback>
                          )}
                        </>
                      )}
                    </Field>
                  </Form.Group>
                </div>
              )}
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Start Date *</Form.Label>
                  <DateSelector name="startOn" isFormik={true} futureOnly={true} />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>End Date *</Form.Label>
                  <DateSelector name="endOn" isFormik={true} futureOnly={true} />
                </Form.Group>
              </div>
            </div>

            <div className="d-flex">
              <div>
                {isEditing && onDelete && (
                  <button type="button" className="btn btn-outline-danger" onClick={onDelete}>
                    Delete
                  </button>
                )}
              </div>
              <div className="ms-auto">
                <button type="button" className="btn btn-secondary me-2" onClick={onCancel}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                  {isEditing ? "Update" : "Create"} Sponsorship
                </button>
              </div>
            </div>
          </Form>
        )
      }}
    </Formik>
  )
}

export default SponsorshipForm
